import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Iconography,
  IconographySizes,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppSelector } from "../../../../redux";
import {
  useSessionPermissionAction,
  useSessionPermissionsLoaded,
  useSessionPermissionsLoading,
} from "../../../../hooks/useSessionPermissions";
import { SETTINGS_PERMISSION_KEYS } from "../../../../utils/permissions/settingsPermissionKeys";
import RolesAndPermissions from "../roles-and-permissions";
import HidUsersTab from "./tabs/HidUsersTab";
import PartnerUsersTab from "./tabs/PartnerUsersTab";
import "./UserManagement.scss";

const USER_SETTINGS_KEY = SETTINGS_PERMISSION_KEYS.USER_SETTINGS;

const USER_MANAGEMENT_TABS = [
  { key: "roles", label: "Roles and Permission" },
  { key: "hid-users", label: "HID Users" },
  { key: "partner-users", label: "Partner Users" },
] as const;

type UserManagementTabKey = (typeof USER_MANAGEMENT_TABS)[number]["key"];

interface UserManagementProps {
  readOnly?: boolean;
  onDetailViewChange?: (isDetailView: boolean) => void;
}

const UserManagement: React.FC<UserManagementProps> = ({
  readOnly = false,
  onDetailViewChange,
}) => {
  const [activeTab, setActiveTab] = useState<UserManagementTabKey>("roles");
  const [isRoleDetailView, setIsRoleDetailView] = useState(false);
  const addNewRoleRef = useRef<(() => void) | null>(null);

  const roleId = useAppSelector((state) => state.auth.loggedUserDetail?.role_id);
  const sessionPermissionsLoading = useSessionPermissionsLoading();
  const sessionPermissionsLoaded = useSessionPermissionsLoaded();
  const userSettingsAction = useSessionPermissionAction(USER_SETTINGS_KEY);

  const sessionGateReady =
    Boolean(roleId) && sessionPermissionsLoaded && !sessionPermissionsLoading;

  const canShowUserTabs = useMemo(() => {
    if (!roleId) return true;
    if (!sessionGateReady) return true;
    return userSettingsAction === "edit" || userSettingsAction === "view";
  }, [roleId, sessionGateReady, userSettingsAction]);

  const userTabsReadOnly = useMemo(() => {
    if (!roleId) return readOnly;
    if (!sessionGateReady) return readOnly;
    return userSettingsAction === "view";
  }, [roleId, sessionGateReady, userSettingsAction, readOnly]);

  const visibleTabs = useMemo(() => {
    return USER_MANAGEMENT_TABS.filter((tab) => {
      if (tab.key === "roles") return true;
      return canShowUserTabs;
    });
  }, [canShowUserTabs]);

  useEffect(() => {
    if (visibleTabs.some((tab) => tab.key === activeTab)) return;
    const fallbackTab = visibleTabs[0]?.key ?? "roles";
    setActiveTab(fallbackTab);
  }, [visibleTabs, activeTab]);

  const handleDetailViewChange = useCallback(
    (isDetailView: boolean) => {
      setIsRoleDetailView(isDetailView);
      onDetailViewChange?.(isDetailView);
    },
    [onDetailViewChange]
  );

  const handleRegisterAddNewRole = useCallback((handler: () => void) => {
    addNewRoleRef.current = handler;
  }, []);

  const tabContent = useMemo(() => {
    switch (activeTab) {
      case "roles":
        return (
          <RolesAndPermissions
            hideHeader
            readOnly={readOnly}
            onDetailViewChange={handleDetailViewChange}
            onRegisterAddNewRole={handleRegisterAddNewRole}
          />
        );
      case "hid-users":
        return <HidUsersTab readOnly={userTabsReadOnly} />;
      case "partner-users":
        return <PartnerUsersTab readOnly={userTabsReadOnly} />;
      default:
        return null;
    }
  }, [
    activeTab,
    readOnly,
    userTabsReadOnly,
    handleDetailViewChange,
    handleRegisterAddNewRole,
  ]);

  return (
    <div
      className={`user-management${
        isRoleDetailView ? " user-management--role-detail" : ""
      }`}
    >
      {!isRoleDetailView && (
        <div className="user-management__tab-bar">
          <div
            className="page-tabs"
            role="tablist"
            aria-label="User management views"
          >
            {visibleTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.key}
                className={`page-tab${
                  activeTab === tab.key ? " page-tab--active" : ""
                }`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {activeTab === "roles" && !readOnly && (
            <Button
              variant="primary"
              label="Add New Role"
              className="user-management__tab-action-btn"
              icon={<Iconography icon="plus" size={IconographySizes.Medium} />}
              onClick={() => addNewRoleRef.current?.()}
            />
          )}
        </div>
      )}
      <div className="user-management__content" role="tabpanel">
        {tabContent}
      </div>
    </div>
  );
};

export default UserManagement;
